// What a company created from the console starts with, so it looks exactly like one created on an iPhone
// (WEB_PLAN.md §5.2). Ports of SeedData.installDefaults and its default lists (GroundTeam/Services/SeedData.swift:53-145)
// and of the items SyncCenter.createCompany / AppStore.completeOnboarding upload (GroundTeam/Sync/SyncCenter.swift:959,
// GroundTeam/Views/Onboarding/Onboarding+Store.swift): the settings (named, onboarded) and the admin's profile.
//
// The server creates no items (`create_company` only makes the company and the admin membership), so after the RPC the
// console pushes `companySeedPatches(...)` in one save. A company without `settings` would send iPhones to onboarding.

import { INTEGRATION_KINDS, type DistanceUnit } from '@/contracts/enums';
import { SETTINGS_ID } from '@/contracts/collections';
import {
  newBadge,
  newCustomField,
  newIntegration,
  newLeadSource,
  newPipeline,
  newSettings,
  newTemplate,
  newUser,
  newVisitResult,
  type BadgePayload,
  type CustomFieldPayload,
  type IntegrationPayload,
  type LeadSourcePayload,
  type PipelinePayload,
  type SettingsInit,
  type SettingsPayload,
  type TemplatePayload,
  type UserPayload,
  type VisitResultPayload,
} from '@/contracts/models';
import type { JsonObject } from '@/contracts/wire';
import { toWireDate } from '@/contracts/wireDate';
import { patch, type Patch } from '@/sync/patch';
import { trimAll } from './util';

/** `SeedData.defaultPipelines()`: Residential Sales (lead), B2B Accounts (account), Contacts (contact), all default. */
export function defaultPipelines(): PipelinePayload[] {
  return [
    newPipeline({
      name: 'Residential Sales',
      recordType: 'lead',
      isDefault: true,
      stages: [
        { name: 'New', colorHex: '#8C919E', order: 0, probability: 5 },
        { name: 'Contacted', colorHex: '#2673E6', order: 1, probability: 15 },
        { name: 'Appointment Set', colorHex: '#7D4DBF', order: 2, probability: 35 },
        { name: 'Proposal', colorHex: '#F5B400', order: 3, probability: 60 },
        { name: 'Won', colorHex: '#29A85C', order: 4, probability: 100, isWon: true },
        { name: 'Lost', colorHex: '#DB4038', order: 5, probability: 0, isLost: true },
      ],
    }),
    newPipeline({
      name: 'B2B Accounts',
      recordType: 'account',
      isDefault: true,
      stages: [
        { name: 'Prospect', colorHex: '#8C919E', order: 0, probability: 10 },
        { name: 'Qualified', colorHex: '#2673E6', order: 1, probability: 25 },
        { name: 'Demo', colorHex: '#7D4DBF', order: 2, probability: 45 },
        { name: 'Negotiation', colorHex: '#F5B400', order: 3, probability: 70 },
        { name: 'Customer', colorHex: '#29A85C', order: 4, probability: 100, isWon: true },
        { name: 'Churned', colorHex: '#DB4038', order: 5, probability: 0, isLost: true },
      ],
    }),
    newPipeline({
      name: 'Contacts',
      recordType: 'contact',
      isDefault: true,
      stages: [
        { name: 'Identified', colorHex: '#8C919E', order: 0, probability: 10 },
        { name: 'Engaged', colorHex: '#2673E6', order: 1, probability: 40 },
        { name: 'Champion', colorHex: '#29A85C', order: 2, probability: 80 },
      ],
    }),
  ];
}

/** `SeedData.defaultVisitResults()`, unlinked. */
function baseVisitResults(): VisitResultPayload[] {
  return [
    newVisitResult({ name: 'Not Home', colorHex: '#8C919E', order: 0, countsAsContact: false }),
    newVisitResult({ name: 'Contacted', colorHex: '#2673E6', order: 1, countsAsContact: true }),
    newVisitResult({ name: 'Not Interested', colorHex: '#DB4038', order: 2, countsAsContact: true }),
    newVisitResult({ name: 'Callback Requested', colorHex: '#F5B400', order: 3, countsAsContact: true, isPositive: true }),
    newVisitResult({ name: 'Appointment Set', colorHex: '#7D4DBF', order: 4, countsAsContact: true, isPositive: true }),
    newVisitResult({ name: 'Left Material', colorHex: '#009E9E', order: 5, countsAsContact: false }),
    newVisitResult({ name: 'Sold', colorHex: '#29A85C', order: 6, countsAsContact: true, isPositive: true }),
    newVisitResult({ name: 'Wrong Address', colorHex: '#8B5E3C', order: 7, countsAsContact: false }),
  ];
}

/**
 * `SeedData.defaultVisitResults(linkedTo:)`: in the first lead pipeline, "Appointment Set" moves to its
 * "Appointment Set" stage, "Sold" to its first won stage and "Contacted" to its "Contacted" stage.
 */
export function defaultVisitResults(linkedTo: readonly PipelinePayload[]): VisitResultPayload[] {
  const results = baseVisitResults();
  const lead = linkedTo.find((p) => p.recordType === 'lead');
  const link = (resultName: string, stageId: string | undefined) => {
    const r = results.find((x) => x.name === resultName);
    if (r && stageId) r.movesToStageId = stageId;
  };
  link('Appointment Set', lead?.stages.find((s) => s.name === 'Appointment Set')?.id);
  link('Sold', lead?.stages.find((s) => s.isWon)?.id);
  link('Contacted', lead?.stages.find((s) => s.name === 'Contacted')?.id);
  return results;
}

/** `SeedData.defaultCustomFields()`. */
export function defaultCustomFields(): CustomFieldPayload[] {
  return [
    newCustomField({ name: 'Home Owner', type: 'checkbox', appliesTo: ['lead'], showOnCard: true, order: 0 }),
    newCustomField({
      name: 'Roof Type',
      type: 'dropdown',
      options: ['Asphalt Shingle', 'Metal', 'Tile', 'Flat'],
      appliesTo: ['lead', 'account'],
      order: 1,
    }),
    newCustomField({ name: 'Year Built', type: 'number', appliesTo: ['lead', 'account'], order: 2 }),
    newCustomField({ name: 'Square Footage', type: 'number', appliesTo: ['lead', 'account'], order: 3 }),
    newCustomField({ name: 'Decision Maker', type: 'text', appliesTo: ['account'], order: 4 }),
    newCustomField({ name: 'Contract Renewal', type: 'date', appliesTo: ['account'], order: 5 }),
  ];
}

/** `SeedData.defaultLeadSources()`. */
export const DEFAULT_LEAD_SOURCE_NAMES = [
  'Door Knock',
  'Referral',
  'Website',
  'Storm Map',
  'Lead Machine',
  'Trade Show',
  'Cold Call',
  'Import',
] as const;

export function defaultLeadSources(): LeadSourcePayload[] {
  return DEFAULT_LEAD_SOURCE_NAMES.map((name) => newLeadSource({ name }));
}

/** `SeedData.defaultTemplates()` (all created at `now`). */
export function defaultTemplates(now: Date = new Date()): TemplatePayload[] {
  const createdAt = toWireDate(now);
  return [
    newTemplate({
      name: 'Intro Email',
      channel: 'email',
      subject: 'Great meeting you, {{first_name}}',
      body: "Hi {{first_name}},\n\nThanks for taking a minute to chat today. I've attached an overview of how {{company_name}} can help at {{address}}. Happy to answer any questions.\n\nBest,\n{{rep_name}}\n{{rep_phone}}",
      createdAt,
    }),
    newTemplate({
      name: 'Sorry I Missed You',
      channel: 'text',
      body: 'Hi {{first_name}}, this is {{rep_name}} with {{company_name}}. Sorry I missed you at {{address}} today — is there a better time to stop by? Text me here anytime.',
      createdAt,
    }),
    newTemplate({
      name: 'Appointment Confirmation',
      channel: 'text',
      body: 'Hi {{first_name}}, confirming our appointment. Reply YES to confirm or let me know if you need to reschedule. — {{rep_name}}, {{company_name}}',
      createdAt,
    }),
    newTemplate({
      name: 'Proposal Follow-Up',
      channel: 'email',
      subject: 'Following up on your proposal',
      body: "Hi {{first_name}},\n\nJust checking in on the proposal for {{company}}. Do you have any questions I can answer? I'd love to get this moving for you.\n\n{{rep_name}}",
      createdAt,
    }),
  ];
}

/** `SeedData.defaultBadges()`. */
export function defaultBadges(): BadgePayload[] {
  return [
    newBadge({ name: 'Door Warrior', systemImage: 'figure.walk', metric: 'visits', threshold: 50, period: 'weekly' }),
    newBadge({ name: 'Conversation Starter', systemImage: 'person.wave.2', metric: 'contacts', threshold: 25, period: 'weekly' }),
    newBadge({ name: 'Closer', systemImage: 'trophy', metric: 'deals', threshold: 3, period: 'monthly' }),
    newBadge({ name: 'Rainmaker', systemImage: 'dollarsign.circle', metric: 'revenue', threshold: 25_000, period: 'monthly' }),
    newBadge({ name: 'Dialer', systemImage: 'phone', metric: 'calls', threshold: 40, period: 'weekly' }),
  ];
}

/** One disabled `IntegrationConfig(kind:)` per `IntegrationKind`, in Swift order. */
export function defaultIntegrations(): IntegrationPayload[] {
  return INTEGRATION_KINDS.map((kind) => newIntegration({ kind }));
}

/** `OnboardingProfile.colorHex`: the admin's avatar color on a new company. */
export const DEFAULT_ADMIN_COLOR = '#102A5E';

/**
 * `SyncCenter.profileName(shared:email:)` without shared names: the email's local part, capitalized ("Admin" for an
 * Apple private-relay address or no email).
 */
export function adminNameFromEmail(email: string | null | undefined): { firstName: string; lastName: string } {
  const e = email ?? '';
  const isRelay = e.endsWith('@privaterelay.appleid.com');
  const local = isRelay ? '' : (e.split('@')[0] ?? '');
  if (local === '') return { firstName: 'Admin', lastName: '' };
  const chars = Array.from(local);
  return { firstName: (chars[0] ?? '').toUpperCase() + chars.slice(1).join(''), lastName: '' };
}

export interface CompanySeedInput {
  /** The `company_name` `create_company` returned (already cleaned by the server). */
  companyName: string;
  admin: {
    /** The `p_profile_id` given to `create_company` (it becomes the admin's `users` item id). */
    profileId: string;
    firstName: string;
    lastName?: string;
    /** The account's email ('' for a phone-only account). */
    email?: string | null;
    /** The account's phone as shown to people. */
    phone?: string | null;
    title?: string;
    colorHex?: string;
  };
  /** Settings the creator chose; omitted keys keep the CompanySettings defaults (USD, miles). */
  currencyCode?: string;
  distanceUnit?: DistanceUnit;
  /** Creation time for every item (tests pass a fixed one). */
  now?: Date;
}

export interface CompanySeed {
  settings: SettingsPayload;
  admin: UserPayload;
  pipelines: PipelinePayload[];
  visitResults: VisitResultPayload[];
  customFields: CustomFieldPayload[];
  leadSources: LeadSourcePayload[];
  templates: TemplatePayload[];
  badges: BadgePayload[];
  integrations: IntegrationPayload[];
}

/** The items of a new company (see `companySeedPatches`). */
export function buildCompanySeed(input: CompanySeedInput): CompanySeed {
  const now = input.now ?? new Date();
  const createdAt = toWireDate(now);
  const settingsInit: SettingsInit = {
    companyName: input.companyName,
    onboardingCompleted: true,
    repsSeeOnlyOwnRecords: true,
  };
  if (input.currencyCode) settingsInit.currencyCode = input.currencyCode;
  if (input.distanceUnit) settingsInit.distanceUnit = input.distanceUnit;
  const a = input.admin;
  const fallback = adminNameFromEmail(a.email);
  const first = trimAll(a.firstName);
  const admin = newUser({
    id: a.profileId,
    firstName: first !== '' ? first : fallback.firstName,
    lastName: first !== '' ? trimAll(a.lastName ?? '') : fallback.lastName,
    email: trimAll(a.email ?? ''),
    phone: trimAll(a.phone ?? ''),
    title: trimAll(a.title ?? ''),
    role: 'admin',
    isActive: true,
    colorHex: a.colorHex ?? DEFAULT_ADMIN_COLOR,
    createdAt,
  });
  const pipelines = defaultPipelines();
  return {
    settings: newSettings(settingsInit),
    admin,
    pipelines,
    visitResults: defaultVisitResults(pipelines),
    customFields: defaultCustomFields(),
    leadSources: defaultLeadSources(),
    templates: defaultTemplates(now),
    badges: defaultBadges(),
    integrations: defaultIntegrations(),
  };
}

const asJson = (v: object): JsonObject => v as unknown as JsonObject;

/**
 * Create patches for a new company, in push order: settings (envelope id SETTINGS_ID, `onboardingCompleted: true`,
 * `repsSeeOnlyOwnRecords: true`), the admin's `users` item, then pipelines, visit results, custom fields, lead
 * sources, templates, badges and one integration per kind. Save them in one `save(...)` right after `create_company`.
 */
export function companySeedPatches(input: CompanySeedInput | CompanySeed): Patch[] {
  const seed = 'settings' in input ? input : buildCompanySeed(input);
  const items = <T extends { id: string }>(collection: Parameters<typeof patch.create>[0], list: readonly T[]) =>
    list.map((item) => patch.create(collection, item.id, asJson(item)));
  return [
    patch.create('settings', SETTINGS_ID, asJson(seed.settings)),
    patch.create('users', seed.admin.id, asJson(seed.admin)),
    ...items('pipelines', seed.pipelines),
    ...items('visitResults', seed.visitResults),
    ...items('customFields', seed.customFields),
    ...items('leadSources', seed.leadSources),
    ...items('templates', seed.templates),
    ...items('badges', seed.badges),
    ...items('integrations', seed.integrations),
  ];
}
